import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Monitor, Smartphone, Globe, Trash2, ShieldOff, Clock, ShieldCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDevicesPage = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDevices = async () => {
    try {
      const res = await axios.get(`${API}/admin/trusted-devices`, { headers: getAuthHeaders() });
      setDevices(res.data);
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDevices(); }, []); // eslint-disable-line

  const handleRevoke = async (deviceId) => {
    if (!window.confirm('Revoke this device? It will require 2FA on next login.')) return;
    try {
      await axios.delete(`${API}/admin/trusted-devices/${deviceId}`, { headers: getAuthHeaders() });
      toast.success('Device revoked');
      fetchDevices();
    } catch (error) {
      toast.error('Failed to revoke device');
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm('Revoke ALL trusted devices? Every login will require 2FA.')) return;
    try {
      await axios.delete(`${API}/admin/trusted-devices`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('All devices revoked');
      fetchDevices();
    } catch (error) {
      toast.error('Failed to revoke devices');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getDeviceIcon = (device) => {
    if (device.is_mobile) return Smartphone;
    return Monitor;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-devices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Trusted Devices</h1>
          <p className="text-[#888] text-sm">Manage devices that can skip 2FA verification</p>
        </div>
        {devices.length > 0 && (
          <Button
            onClick={handleRevokeAll}
            variant="outline"
            className="border-red-800 text-red-400 hover:bg-red-900/20 hover:text-red-300"
            data-testid="revoke-all-btn"
          >
            <ShieldOff className="w-4 h-4 mr-1" /> Revoke All
          </Button>
        )}
      </div>

      {/* Info */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#D4540A] mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-[#ccc]">Trusted devices skip the 2FA verification step for 30 days.</p>
            <p className="text-[#666] mt-1">If a device is lost or compromised, revoke it immediately. Revoking all devices forces 2FA on every login.</p>
          </div>
        </div>
      </div>

      {/* Devices List */}
      {devices.length === 0 ? (
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-12 text-center">
          <Monitor className="w-12 h-12 text-[#555] mx-auto mb-3" />
          <p className="text-[#888]">No trusted devices</p>
          <p className="text-[#555] text-sm mt-1">Trust a device during login to skip 2FA on future visits</p>
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map((device) => {
            const Icon = getDeviceIcon(device);
            return (
              <div
                key={device.id}
                className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4 flex items-center gap-4"
                data-testid={`device-${device.id}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-[#888]" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-white font-medium">{device.browser || 'Unknown Browser'}</p>
                    <span className="text-[#555]">&middot;</span>
                    <p className="text-[#888] text-sm">{device.os || 'Unknown OS'}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#666]">
                    <span className="flex items-center gap-1">
                      <Globe className="w-3 h-3" /> {device.ip_address || '—'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Created {formatDate(device.created_at)}
                    </span>
                    <span>Last used {formatDate(device.last_used)}</span>
                  </div>
                </div>

                <Button
                  onClick={() => handleRevoke(device.id)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                  data-testid={`revoke-${device.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Revoke
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDevicesPage;
